import styled from 'styled-components';

//helpers
import { screen } from '@/helpers/screen';

//miranda tokens
import { getToken } from '@loadsmart/miranda-tokens';

//theme
import { homeColor } from '@/theme/ai';

export const Stage = styled.div`
  margin-top: 32px;
  border: 1px solid ${homeColor.line};
  border-radius: 20px;
  background: rgba(234, 244, 241, 0.04);
  display: flex;
  flex-direction: column;

  ${screen.lg} {
    margin-top: 38px;
  }
`;

export const StageHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 20px 18px;
  border-bottom: 1px solid rgba(234, 244, 241, 0.08);

  ${screen.md} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    padding: 24px 28px 20px;
  }
`;

export const StageHeadText = styled.div`
  max-width: 620px;
`;

export const StageHeadTitle = styled.p`
  font-family: ${getToken('font-family-default')};
  font-weight: 600;
  font-size: 16px;
  line-height: 1.3;
  color: ${homeColor.white};
  margin: 0;

  ${screen.lg} {
    font-size: 18px;
  }
`;

export const StageHeadMeta = styled.p`
  font-family: ${getToken('font-family-default')};
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.04em;
  color: ${homeColor.muted2};
  margin: 8px 0 0;
`;

export const StagePill = styled.span`
  flex: none;
  font-family: ${getToken('font-family-default')};
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.04em;
  border-radius: 999px;
  padding: 8px 16px;
  white-space: nowrap;
  transition:
    color 0.35s,
    background 0.35s,
    border-color 0.35s;
  color: ${homeColor.cyan};
  background: rgba(0, 215, 215, 0.08);
  border: 1px solid rgba(0, 215, 215, 0.45);

  ${(props) =>
    props.$done &&
    `
    color: ${homeColor.green};
    background: rgba(20, 213, 77, 0.1);
    border-color: rgba(20, 213, 77, 0.45);
  `}
`;

export const Log = styled.div`
  padding: 14px 20px 4px;
  display: flex;
  flex-direction: column;

  ${screen.md} {
    padding: 16px 28px 6px;
  }
`;

export const LogRow = styled.div`
  display: grid;
  grid-template-columns: 68px 1fr;
  gap: 14px;
  align-items: baseline;
  padding: 10px 0;
  opacity: 0;
  transform: translateY(6px);
  transition:
    opacity 0.45s ease,
    transform 0.45s ease;

  ${screen.md} {
    grid-template-columns: 92px 1fr;
    gap: 20px;
  }

  ${(props) =>
    props.$active &&
    `
    opacity: 1;
    transform: none;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const LogTime = styled.span`
  font-family: ${getToken('font-family-default')};
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  font-weight: 500;
  color: ${homeColor.muted2};
`;

export const LogText = styled.span`
  font-family: ${getToken('font-family-default')};
  font-size: 13px;
  line-height: 1.45;
  color: ${(props) => (props.$last ? homeColor.white : homeColor.muted)};
  font-weight: ${(props) => (props.$last ? 600 : 500)};

  ${screen.md} {
    font-size: 14px;
  }
`;

export const Summary = styled.div`
  margin: 8px 20px 0;
  border: 1px solid ${homeColor.line2};
  border-radius: 16px;
  background: rgba(8, 43, 26, 0.35);
  padding: 16px 20px;
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 0.55s ease,
    transform 0.55s ease;

  ${screen.md} {
    margin: 8px 28px 0;
    padding: 18px 24px;
  }

  ${(props) =>
    props.$active &&
    `
    opacity: 1;
    transform: none;
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const SummaryLabel = styled.p`
  font-family: ${getToken('font-family-default')};
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${homeColor.green};
  margin: 0;
`;

export const SummaryText = styled.p`
  font-family: ${getToken('font-family-default')};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  color: ${homeColor.muted};
  margin: 8px 0 0;
`;

export const StageFoot = styled.div`
  margin-top: auto;
  padding: 16px 20px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  ${screen.md} {
    padding: 18px 28px 22px;
  }
`;

export const StageFootSource = styled.span`
  font-family: ${getToken('font-family-default')};
  font-weight: 500;
  font-size: 12px;
  color: ${homeColor.muted2};
`;

export const StageFootRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const AgentBadge = styled.span`
  font-family: ${getToken('font-family-default')};
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${homeColor.green};
  border: 1px solid ${homeColor.line2};
  border-radius: 999px;
  padding: 6px 16px;
`;

export const ReplayButton = styled.button`
  font-family: ${getToken('font-family-default')};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${homeColor.green};
  background: transparent;
  border: 1px solid ${homeColor.line2};
  border-radius: 999px;
  padding: 6px 14px;
  cursor: pointer;

  &:hover {
    background: rgba(20, 214, 77, 0.08);
  }
`;

export const EscalationNote = styled.div`
  margin-top: 24px;
  border: 1px solid rgba(245, 181, 68, 0.8);
  border-radius: 14px;
  padding: 22px 20px;
  background:
    linear-gradient(90deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%),
    linear-gradient(
      90deg,
      rgba(245, 181, 68, 0.07) 0%,
      rgba(245, 181, 68, 0.02) 60%,
      rgba(245, 181, 68, 0) 100%
    );

  ${screen.lg} {
    padding: 28px 34px;
  }
`;

export const EscalationTitle = styled.p`
  font-family: ${getToken('font-family-default')};
  font-weight: 700;
  font-size: 16px;
  color: #ff9a1a;
  margin: 0 0 8px;
`;

export const EscalationText = styled.p`
  font-family: ${getToken('font-family-default')};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.6;
  color: ${homeColor.white};
  margin: 0;

  ${screen.lg} {
    font-size: 15px;
  }
`;
